import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  IconButton,
  Tooltip,
  alpha,
  Chip,
  SelectChangeEvent,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Breadcrumb from 'components/Breadcrumb';
import { logger } from 'utils/logger';

// Import services
import hxcStagingService, { StagedUser, CrossAccountWarning } from './services/HxcStagingService';
import hxcOnboardingService, { OnboardingUser } from './services/HxcStagingService';
import { accountsService } from '../accounts/services/AccountsService';

// Import components
import StagingTable from './components/StagingTable';
import OnboardingTable from './components/OnboardingTable';
import StagingUserFormModal from './components/StagingUserFormModal';
import ProcessStagingDialog from './components/ProcessStagingDialog';
import ProcessOnboardingDialog from './components/ProcessOnboardingDialog';
import EditOnboardingDateDialog from './components/EditOnboardingDateDialog';
import DeleteOnboardingDialog from './components/DeleteOnboardingDialog';
import StagingFilterDrawer from './components/StagingFilterDrawer';
import OnboardingFilterDrawer from './components/OnboardingFilterDrawer';
import CSVUploadDialog from './components/CSVUploadDialog';

// Import hooks
import useStagingFilter from './hooks/useStagingFilter';
import useOnboardingFilter from './hooks/useOnboardingFilter';

// Create a component-specific logger
const log = logger.getLogger('HXCPartnerCreationPage');

type PageMode = 'staging' | 'onboarding';

const HXCPartnerCreationPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useParams<{ mode: PageMode }>();
  
  // State for current mode
  const [currentMode, setCurrentMode] = useState<PageMode>(mode || 'staging');
  
  // State for data
  const [stagingUsers, setStagingUsers] = useState<StagedUser[]>([]);
  const [onboardingUsers, setOnboardingUsers] = useState<OnboardingUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for selected users (staging mode only)
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  
  // Pagination state
  const [stagingPage, setStagingPage] = useState<number>(0);
  const [stagingRowsPerPage, setStagingRowsPerPage] = useState<number>(25);
  const [onboardingPage, setOnboardingPage] = useState<number>(0);
  const [onboardingRowsPerPage, setOnboardingRowsPerPage] = useState<number>(25);
  
  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingUser, setEditingUser] = useState<StagedUser | null>(null);
  
  // State for process dialog
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  
  // State for process onboarding dialog
  const [processOnboardingDialogOpen, setProcessOnboardingDialogOpen] = useState(false);
  
  // State for edit onboarding date dialog
  const [editDateDialogOpen, setEditDateDialogOpen] = useState(false);
  const [editingOnboardingUser, setEditingOnboardingUser] = useState<OnboardingUser | null>(null);
  
  // State for delete onboarding dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingOnboardingUser, setDeletingOnboardingUser] = useState<OnboardingUser | null>(null);
  
  // State for CSV upload dialog
  const [csvUploadDialogOpen, setCsvUploadDialogOpen] = useState(false);

  // Runtime map of staged user ID -> cross-account warning (clears on navigation/refresh)
  const [crossAccountWarnings, setCrossAccountWarnings] = useState<Map<string, CrossAccountWarning>>(new Map());
  
  // Use filter hooks
  const stagingFilter = useStagingFilter();
  const onboardingFilter = useOnboardingFilter();
  
  // Update current mode when URL parameter changes
  useEffect(() => {
    if (mode && mode !== currentMode) {
      setCurrentMode(mode);
    }
  }, [mode, currentMode]);
  
  // Handle mode change
  const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: PageMode | null) => {
    if (newMode !== null) {
      setCurrentMode(newMode);
      navigate(`/hxc-partners/${newMode}`);
      // Clear selections when switching modes
      setSelectedUserIds([]);
    }
  };
  
  // Fetch staging users
  const fetchStagingUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      log.log('Fetching staging users...');
      
      const response = await hxcStagingService.getStagedUsers();
      setStagingUsers(response.users);
      log.log(`Successfully fetched ${response.count} staging users`);
    } catch (err) {
      log.error('Error fetching staging users:', err);
      setError('Failed to load staging users');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch onboarding users
  const fetchOnboardingUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      log.log('Fetching onboarding users...');
      
      const response = await hxcOnboardingService.getOnboardingUsers();
      setOnboardingUsers(response.users);
      log.log(`Successfully fetched ${response.count} onboarding users`);
    } catch (err) {
      log.error('Error fetching onboarding users:', err);
      setError('Failed to load onboarding users');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch data when mode changes
  useEffect(() => {
    if (currentMode === 'staging') {
      fetchStagingUsers();
    } else {
      fetchOnboardingUsers();
    }
  }, [currentMode]);
  
  // Filter staging users based on filter state
  const filteredStagingUsers = useMemo(() => {
    let filtered = [...stagingUsers];
    
    if (stagingFilter.filterState.accountName) {
      const searchTerm = stagingFilter.filterState.accountName.toLowerCase();
      filtered = filtered.filter(user => 
        user.accountName.toLowerCase().includes(searchTerm)
      );
    }
    
    if (stagingFilter.filterState.sfdcAccountId) {
      const searchTerm = stagingFilter.filterState.sfdcAccountId.toLowerCase();
      filtered = filtered.filter(user => 
        user.sfdcAccountId.toLowerCase().includes(searchTerm)
      );
    }
    
    if (stagingFilter.filterState.fullName) {
      const searchTerm = stagingFilter.filterState.fullName.toLowerCase();
      filtered = filtered.filter(user => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return fullName.includes(searchTerm);
      });
    }
    
    if (stagingFilter.filterState.email) {
      const searchTerm = stagingFilter.filterState.email.toLowerCase();
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(searchTerm)
      );
    }
    
    return filtered;
  }, [stagingUsers, stagingFilter.filterState]);

  // Helper function to parse date string as local date (not UTC)
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  
  // Filter onboarding users based on filter state
  const filteredOnboardingUsers = useMemo(() => {
    let filtered = [...onboardingUsers];
    
    if (onboardingFilter.filterState.username) {
      const searchTerm = onboardingFilter.filterState.username.toLowerCase();
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(searchTerm)
      );
    }
    
    if (onboardingFilter.filterState.fullName) {
      const searchTerm = onboardingFilter.filterState.fullName.toLowerCase();
      filtered = filtered.filter(user => 
        user.fullname.toLowerCase().includes(searchTerm)
      );
    }
    
    // Filter by onboard date range (compare only date parts, not time)
    if (onboardingFilter.filterState.onboardDateFrom) {
      const fromDate = parseLocalDate(onboardingFilter.filterState.onboardDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(user => {
        if (!user.onboardDate) return false;
        const userDate = new Date(user.onboardDate);
        userDate.setHours(0, 0, 0, 0);
        return userDate >= fromDate;
      });
    }
    
    if (onboardingFilter.filterState.onboardDateTo) {
      const toDate = parseLocalDate(onboardingFilter.filterState.onboardDateTo);
      toDate.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(user => {
        if (!user.onboardDate) return false;
        const userDate = new Date(user.onboardDate);
        userDate.setHours(0, 0, 0, 0);
        return userDate <= toDate;
      });
    }
    
    // Filter by email sent date range (compare only date parts, not time)
    if (onboardingFilter.filterState.emailSentDateFrom) {
      const fromDate = parseLocalDate(onboardingFilter.filterState.emailSentDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(user => {
        if (!user.emailSentDate) return false;
        const userDate = new Date(user.emailSentDate);
        userDate.setHours(0, 0, 0, 0);
        return userDate >= fromDate;
      });
    }
    
    if (onboardingFilter.filterState.emailSentDateTo) {
      const toDate = parseLocalDate(onboardingFilter.filterState.emailSentDateTo);
      toDate.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(user => {
        if (!user.emailSentDate) return false;
        const userDate = new Date(user.emailSentDate);
        userDate.setHours(0, 0, 0, 0);
        return userDate <= toDate;
      });
    }
    
    return filtered;
  }, [onboardingUsers, onboardingFilter.filterState]);
  
  // Paginate staging users
  const paginatedStagingUsers = useMemo(() => {
    const startIndex = stagingPage * stagingRowsPerPage;
    const endIndex = startIndex + stagingRowsPerPage;
    return filteredStagingUsers.slice(startIndex, endIndex);
  }, [filteredStagingUsers, stagingPage, stagingRowsPerPage]);
  
  // Paginate onboarding users
  const paginatedOnboardingUsers = useMemo(() => {
    const startIndex = onboardingPage * onboardingRowsPerPage;
    const endIndex = startIndex + onboardingRowsPerPage;
    return filteredOnboardingUsers.slice(startIndex, endIndex);
  }, [filteredOnboardingUsers, onboardingPage, onboardingRowsPerPage]);
  
  // Handle staging page change
  const handleStagingPageChange = (newPage: number) => {
    setStagingPage(newPage);
  };
  
  // Handle staging rows per page change
  const handleStagingRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setStagingRowsPerPage(Number(event.target.value));
    setStagingPage(0); // Reset to first page
  };
  
  // Handle onboarding page change
  const handleOnboardingPageChange = (newPage: number) => {
    setOnboardingPage(newPage);
  };
  
  // Handle onboarding rows per page change
  const handleOnboardingRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setOnboardingRowsPerPage(Number(event.target.value));
    setOnboardingPage(0); // Reset to first page
  };
  
  // Get pagination info for staging
  const getStagingPaginationInfo = () => {
    if (filteredStagingUsers.length === 0) {
      return '0 items';
    }
    const startItem = stagingPage * stagingRowsPerPage + 1;
    const endItem = Math.min((stagingPage + 1) * stagingRowsPerPage, filteredStagingUsers.length);
    return `${startItem} - ${endItem} of ${filteredStagingUsers.length} items`;
  };
  
  // Get pagination info for onboarding
  const getOnboardingPaginationInfo = () => {
    if (filteredOnboardingUsers.length === 0) {
      return '0 items';
    }
    const startItem = onboardingPage * onboardingRowsPerPage + 1;
    const endItem = Math.min((onboardingPage + 1) * onboardingRowsPerPage, filteredOnboardingUsers.length);
    return `${startItem} - ${endItem} of ${filteredOnboardingUsers.length} items`;
  };
  
  // Handle opening modal for creating a new user
  const handleAddUser = () => {
    setModalMode('create');
    setEditingUser(null);
    setModalOpen(true);
  };
  
  // Handle opening modal for editing a user
  const handleEditUser = (user: StagedUser) => {
    setModalMode('edit');
    setEditingUser(user);
    setModalOpen(true);
  };
  
  // Handle closing modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };
  
  // Handle form submit (create)
  const handleCreateUser = async (userData: Omit<StagedUser, 'id'>) => {
    try {
      log.log('Creating new staged user:', userData);
      await hxcStagingService.addStagedUser(userData);
      await fetchStagingUsers();
      log.log('Successfully created staged user');
    } catch (err) {
      log.error('Error creating staged user:', err);
      throw err;
    }
  };
  
  // Handle form submit (update)
  const handleUpdateUser = async (userData: Omit<StagedUser, 'id'>) => {
    if (!editingUser) return;
    
    try {
      log.log('Updating staged user:', editingUser.id);
      const updatedUser: StagedUser = {
        ...userData,
        id: editingUser.id
      };
      await hxcStagingService.updateStagedUser(updatedUser);
      await fetchStagingUsers();
      log.log('Successfully updated staged user');
    } catch (err) {
      log.error('Error updating staged user:', err);
      throw err;
    }
  };
  
  // Handle delete user
  const handleDeleteUser = async (user: StagedUser) => {
    try {
      log.log('Deleting staged user:', user.id);
      await hxcStagingService.deleteStagedUser(user.id);
      await fetchStagingUsers();
      log.log('Successfully deleted staged user');
      // Clear selection if deleted user was selected
      setSelectedUserIds(prev => prev.filter(id => id !== user.id));
    } catch (err) {
      log.error('Error deleting staged user:', err);
      throw err;
    }
  };
  
  // Handle opening process dialog
  const handleOpenProcessDialog = () => {
    setProcessDialogOpen(true);
  };
  
  // Handle closing process dialog
  const handleCloseProcessDialog = (isClosed: boolean) => {
    setProcessDialogOpen(false);
    // Clear selection and refresh after processing
    if (!isClosed) {
      setSelectedUserIds([]);
      fetchStagingUsers();
    }
  };
  
  // Handle processing staged users
  const handleProcessUsers = async (userIds: number[], onboardDate?: string, allowMigrate?: boolean) => {
    try {
      log.log('Processing staged users:', userIds, 'with onboard date:', onboardDate || 'ASAP', 'allowMigrate:', allowMigrate || false);
      const result = await hxcStagingService.processStagedUsers(userIds, onboardDate, allowMigrate);
      log.log('Processing result:', result);

      // Build cross-account warnings for users skipped due to existing in another account
      const differentAccountSkips = result.skippedUsers.filter(
        s => s.reason === 'User is found within a different Account'
      );
      if (differentAccountSkips.length > 0) {
        const uniqueAccountIds = differentAccountSkips
          .map(s => s.existingAccountId)
          .filter((id, index, arr) => arr.indexOf(id) === index);
        const accountNameMap = new Map<number, string>();
        await Promise.all(
          uniqueAccountIds.map(async (accountId) => {
            try {
              const acct = await accountsService.getAccount(accountId);
              accountNameMap.set(accountId, acct.name);
            } catch {
              accountNameMap.set(accountId, `Account #${accountId}`);
            }
          })
        );

        // Build email -> staging ID map from current staged users
        const emailToStagingId = new Map(stagingUsers.map(u => [u.email.toLowerCase(), u.id]));

        setCrossAccountWarnings(prev => {
          const next = new Map(prev);
          differentAccountSkips.forEach(skipped => {
            const stagingId = emailToStagingId.get(skipped.userEmail.toLowerCase());
            if (stagingId) {
              next.set(stagingId, {
                existingAccountId: skipped.existingAccountId,
                existingAccountName: accountNameMap.get(skipped.existingAccountId) ?? `Account #${skipped.existingAccountId}`
              });
            }
          });
          return next;
        });
      }

      return { processedUsers: result.processedUsers, skippedUsers: result.skippedUsers };
    } catch (err) {
      log.error('Error processing staged users:', err);
      throw err;
    }
  };
  
  // Handle opening process onboarding dialog
  const handleOpenProcessOnboardingDialog = () => {
    setProcessOnboardingDialogOpen(true);
  };
  
  // Handle closing process onboarding dialog
  const handleCloseProcessOnboardingDialog = () => {
    setProcessOnboardingDialogOpen(false);
    // Refresh onboarding list after processing
    fetchOnboardingUsers();
  };
  
  // Handle processing onboarding users
  const handleProcessOnboarding = async () => {
    try {
      log.log('Processing onboarding - sending emails');
      const result = await hxcOnboardingService.processOnboarding();
      log.log('Processing result:', result);
      return { emailCount: result.onboardedUsers.length };
    } catch (err) {
      log.error('Error processing onboarding:', err);
      throw err;
    }
  };
  
  // Handle opening edit date dialog
  const handleEditOnboardingDate = (user: OnboardingUser) => {
    setEditingOnboardingUser(user);
    setEditDateDialogOpen(true);
  };
  
  // Handle closing edit date dialog
  const handleCloseEditDateDialog = () => {
    setEditDateDialogOpen(false);
    setEditingOnboardingUser(null);
    // Refresh onboarding list after edit
    fetchOnboardingUsers();
  };
  
  // Handle saving edited onboard date
  const handleSaveOnboardDate = async (userId: number, onboardDate: string) => {
    try {
      log.log(`Updating onboard date for user ${userId} to:`, onboardDate);
      await hxcOnboardingService.updateOnboardingUser(userId, onboardDate);
      log.log('Successfully updated onboard date');
    } catch (err) {
      log.error('Error updating onboard date:', err);
      throw err;
    }
  };
  
  // Handle opening delete dialog
  const handleDeleteOnboardingUser = (user: OnboardingUser) => {
    setDeletingOnboardingUser(user);
    setDeleteDialogOpen(true);
  };
  
  // Handle closing delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingOnboardingUser(null);
    // Refresh onboarding list after delete
    fetchOnboardingUsers();
  };
  
  // Handle deleting onboarding user
  const handleConfirmDelete = async (userId: number) => {
    try {
      log.log(`Deleting onboarding user with ID: ${userId}`);
      await hxcOnboardingService.deleteOnboardingUser(userId);
      log.log('Successfully deleted onboarding user');
    } catch (err) {
      log.error('Error deleting onboarding user:', err);
      throw err;
    }
  };
  
  // Handle opening CSV upload dialog
  const handleOpenCsvUpload = () => {
    setCsvUploadDialogOpen(true);
  };
  
  // Handle closing CSV upload dialog
  const handleCloseCsvUpload = () => {
    setCsvUploadDialogOpen(false);
  };
  
  // Get selected user objects
  const getSelectedUsers = (): StagedUser[] => {
    return stagingUsers.filter(user => selectedUserIds.includes(user.id));
  };
  
  return (
    <Container maxWidth={false}>
      <Breadcrumb 
        items={[
          { label: 'HXC Partner Creation', path: `/hxc-partners/${currentMode}` }
        ]} 
      />
      
      <Typography 
        variant="h4" 
        component="h1"
        sx={{ 
          fontWeight: 600, 
          color: theme.palette.primary.main,
          mb: 3
        }}
      >
        HXC Partner Creation
      </Typography>
      
      {error && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: theme.palette.error.light }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}
      
      {loading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </Paper>
      ) : (
        <Paper 
          elevation={2}
          sx={{ 
            width: '100%', 
            mb: 3, 
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          {/* Action Section */}
          <Box 
            p={2.5} 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default
            }}
          >
            {/* Left side - Filter chips, Filter button, and Add button */}
            <Box display="flex" alignItems="center" gap={1.5}>
              {/* Filter chips */}
              {currentMode === 'staging' && stagingFilter.filterState.activeFilters.length > 0 && (
                <Box display="flex" flexWrap="wrap" alignItems="center" mr={0.5}>
                  {stagingFilter.filterState.activeFilters.map((filter, index) => (
                    <Chip 
                      key={index} 
                      label={filter} 
                      size="small"
                      onDelete={() => stagingFilter.removeFilter(filter)} 
                      sx={{ 
                        mr: 0.7, 
                        mb: 0.5,
                        borderRadius: '16px',
                        fontWeight: 500,
                        transition: 'all 0.2s',
                        '&:hover': {
                          opacity: 0.9,
                          transform: 'translateY(-1px)'
                        }
                      }} 
                    />
                  ))}
                </Box>
              )}
              
              {currentMode === 'onboarding' && onboardingFilter.filterState.activeFilters.length > 0 && (
                <Box display="flex" flexWrap="wrap" alignItems="center" mr={0.5}>
                  {onboardingFilter.filterState.activeFilters.map((filter, index) => (
                    <Chip 
                      key={index} 
                      label={filter} 
                      size="small"
                      onDelete={() => onboardingFilter.removeFilter(filter)} 
                      sx={{ 
                        mr: 0.7, 
                        mb: 0.5,
                        borderRadius: '16px',
                        fontWeight: 500,
                        transition: 'all 0.2s',
                        '&:hover': {
                          opacity: 0.9,
                          transform: 'translateY(-1px)'
                        }
                      }} 
                    />
                  ))}
                </Box>
              )}
              
              <Tooltip title="Filter">
                <IconButton 
                  size="medium"
                  onClick={() => {
                    if (currentMode === 'staging') {
                      stagingFilter.setFilterDrawerOpen(true);
                    } else {
                      onboardingFilter.setFilterDrawerOpen(true);
                    }
                  }}
                  sx={{ 
                    borderRadius: '12px',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                    }
                  }}
                >
                  <FilterAltIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Refresh Table">
                <IconButton 
                  size="medium"
                  onClick={() => {
                    if (currentMode === 'staging') {
                      fetchStagingUsers();
                    } else {
                      fetchOnboardingUsers();
                    }
                  }}
                  sx={{ 
                    borderRadius: '12px',
                    transition: 'all 0.2s',
                    color: theme.palette.primary.main,
                    '&:hover': { 
                      backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              
              {currentMode === 'staging' && (
                <>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="medium"
                    onClick={handleAddUser}
                    startIcon={<AddIcon />}
                    sx={{ 
                      px: 2,
                      py: 1,
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                      }
                    }}
                  >
                    Add User
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="medium"
                    onClick={handleOpenCsvUpload}
                    startIcon={<CloudUploadIcon />}
                    sx={{ 
                      px: 2,
                      py: 1,
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    Upload .CSV
                  </Button>
                </>
              )}
            </Box>
            
            {/* Right side - Process and Mode toggle */}
            <Box display="flex" alignItems="center" gap={2}>
              {currentMode === 'staging' && (
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleOpenProcessDialog}
                  disabled={selectedUserIds.length === 0}
                  sx={{ 
                    px: 2,
                    py: 1,
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                    }
                  }}
                >
                  Process Staging
                </Button>
              )}
              
              {currentMode === 'onboarding' && (
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleOpenProcessOnboardingDialog}
                  sx={{ 
                    px: 2,
                    py: 1,
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                    }
                  }}
                >
                  Send Emails
                </Button>
              )}
              
              <ToggleButtonGroup
                value={currentMode}
                exclusive
                onChange={handleModeChange}
                aria-label="page mode"
                size="small"
              >
                <ToggleButton value="staging" aria-label="staging mode">
                  Staging
                </ToggleButton>
                <ToggleButton value="onboarding" aria-label="onboarding mode">
                  Onboarding
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          
          {/* Table */}
          {currentMode === 'staging' ? (
            <>
              <StagingTable
                users={paginatedStagingUsers}
                selectedIds={selectedUserIds}
                onSelectionChange={setSelectedUserIds}
                onRefresh={fetchStagingUsers}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                crossAccountWarnings={crossAccountWarnings}
              />
              
              {/* Staging Pagination */}
              <Box 
                sx={{ 
                  px: 3, 
                  py: 2, 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  backgroundColor: theme.palette.background.default
                }}
              >
                <Box display="flex" alignItems="center">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mr: 2,
                      color: alpha(theme.palette.text.primary, 0.7),
                      fontWeight: 500
                    }}
                  >
                    Items per page:
                  </Typography>
                  <FormControl 
                    size="small" 
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }
                    }}
                  >
                    <Select
                      value={stagingRowsPerPage}
                      onChange={handleStagingRowsPerPageChange}
                      sx={{ 
                        height: 36, 
                        minWidth: 70,
                        fontWeight: 500,
                        '& .MuiSelect-select': { 
                          py: 0.7, 
                          px: 1.5 
                        } 
                      }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box display="flex" alignItems="center">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: alpha(theme.palette.text.primary, 0.7) 
                    }}
                  >
                    {getStagingPaginationInfo()}
                  </Typography>
                  <Box ml={1.5} display="flex" alignItems="center">
                    <Tooltip title="Previous page">
                      <span>
                        <IconButton 
                          disabled={stagingPage === 0} 
                          onClick={() => handleStagingPageChange(stagingPage - 1)}
                          sx={{ 
                            mx: 0.5,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            transition: 'all 0.2s',
                            color: stagingPage === 0 ? undefined : theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08)
                            }
                          }}
                          size="small"
                        >
                          <NavigateBeforeIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    
                    <Tooltip title="Next page">
                      <span>
                        <IconButton 
                          disabled={(stagingPage + 1) * stagingRowsPerPage >= filteredStagingUsers.length}
                          onClick={() => handleStagingPageChange(stagingPage + 1)}
                          sx={{ 
                            mx: 0.5,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            transition: 'all 0.2s',
                            color: (stagingPage + 1) * stagingRowsPerPage >= filteredStagingUsers.length 
                              ? undefined : theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08)
                            }
                          }}
                          size="small"
                        >
                          <NavigateNextIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <OnboardingTable
                users={paginatedOnboardingUsers}
                onRefresh={fetchOnboardingUsers}
                onEditDate={handleEditOnboardingDate}
                onDelete={handleDeleteOnboardingUser}
              />
              
              {/* Onboarding Pagination */}
              <Box 
                sx={{ 
                  px: 3, 
                  py: 2, 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  backgroundColor: theme.palette.background.default
                }}
              >
                <Box display="flex" alignItems="center">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mr: 2,
                      color: alpha(theme.palette.text.primary, 0.7),
                      fontWeight: 500
                    }}
                  >
                    Items per page:
                  </Typography>
                  <FormControl 
                    size="small" 
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }
                    }}
                  >
                    <Select
                      value={onboardingRowsPerPage}
                      onChange={handleOnboardingRowsPerPageChange}
                      sx={{ 
                        height: 36, 
                        minWidth: 70,
                        fontWeight: 500,
                        '& .MuiSelect-select': { 
                          py: 0.7, 
                          px: 1.5 
                        } 
                      }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box display="flex" alignItems="center">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: alpha(theme.palette.text.primary, 0.7) 
                    }}
                  >
                    {getOnboardingPaginationInfo()}
                  </Typography>
                  <Box ml={1.5} display="flex" alignItems="center">
                    <Tooltip title="Previous page">
                      <span>
                        <IconButton 
                          disabled={onboardingPage === 0} 
                          onClick={() => handleOnboardingPageChange(onboardingPage - 1)}
                          sx={{ 
                            mx: 0.5,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            transition: 'all 0.2s',
                            color: onboardingPage === 0 ? undefined : theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08)
                            }
                          }}
                          size="small"
                        >
                          <NavigateBeforeIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    
                    <Tooltip title="Next page">
                      <span>
                        <IconButton 
                          disabled={(onboardingPage + 1) * onboardingRowsPerPage >= filteredOnboardingUsers.length}
                          onClick={() => handleOnboardingPageChange(onboardingPage + 1)}
                          sx={{ 
                            mx: 0.5,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            transition: 'all 0.2s',
                            color: (onboardingPage + 1) * onboardingRowsPerPage >= filteredOnboardingUsers.length 
                              ? undefined : theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08)
                            }
                          }}
                          size="small"
                        >
                          <NavigateNextIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      )}
      
      {/* Staging Filter Drawer */}
      <StagingFilterDrawer
        open={stagingFilter.filterDrawerOpen}
        onClose={() => stagingFilter.setFilterDrawerOpen(false)}
        tempState={stagingFilter.tempState}
        expandedAccordions={stagingFilter.expandedAccordions}
        setExpandedAccordions={stagingFilter.setExpandedAccordions}
        handleAccountNameChange={stagingFilter.handleAccountNameChange}
        handleSfdcAccountIdChange={stagingFilter.handleSfdcAccountIdChange}
        handleFullNameChange={stagingFilter.handleFullNameChange}
        handleEmailChange={stagingFilter.handleEmailChange}
        applyFilters={stagingFilter.applyFilters}
        clearFilters={stagingFilter.clearFilters}
      />
      
      {/* Onboarding Filter Drawer */}
      <OnboardingFilterDrawer
        open={onboardingFilter.filterDrawerOpen}
        onClose={() => onboardingFilter.setFilterDrawerOpen(false)}
        tempState={onboardingFilter.tempState}
        expandedAccordions={onboardingFilter.expandedAccordions}
        setExpandedAccordions={onboardingFilter.setExpandedAccordions}
        handleUsernameChange={onboardingFilter.handleUsernameChange}
        handleFullNameChange={onboardingFilter.handleFullNameChange}
        handleOnboardDateFromChange={onboardingFilter.handleOnboardDateFromChange}
        handleOnboardDateToChange={onboardingFilter.handleOnboardDateToChange}
        handleEmailSentDateFromChange={onboardingFilter.handleEmailSentDateFromChange}
        handleEmailSentDateToChange={onboardingFilter.handleEmailSentDateToChange}
        applyFilters={onboardingFilter.applyFilters}
        clearFilters={onboardingFilter.clearFilters}
      />
      
      {/* User Form Modal */}
      <StagingUserFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={modalMode === 'create' ? handleCreateUser : handleUpdateUser}
        user={editingUser}
        mode={modalMode}
        allUsers={stagingUsers}
      />
      
      {/* Process Staging Dialog */}
      <ProcessStagingDialog
        open={processDialogOpen}
        onClose={handleCloseProcessDialog}
        selectedUsers={getSelectedUsers()}
        onProcess={handleProcessUsers}
        crossAccountWarnings={crossAccountWarnings}
      />
      
      {/* Process Onboarding Dialog */}
      <ProcessOnboardingDialog
        open={processOnboardingDialogOpen}
        onClose={handleCloseProcessOnboardingDialog}
        users={onboardingUsers}
        onProcess={handleProcessOnboarding}
      />
      
      {/* Edit Onboarding Date Dialog */}
      <EditOnboardingDateDialog
        open={editDateDialogOpen}
        onClose={handleCloseEditDateDialog}
        user={editingOnboardingUser}
        onSave={handleSaveOnboardDate}
      />
      
      {/* Delete Onboarding Dialog */}
      <DeleteOnboardingDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        user={deletingOnboardingUser}
        onDelete={handleConfirmDelete}
      />
      
      {/* CSV Upload Dialog */}
      <CSVUploadDialog
        open={csvUploadDialogOpen}
        onClose={handleCloseCsvUpload}
        existingUsers={stagingUsers}
        onCreateUser={hxcStagingService.addStagedUser}
        onRefresh={fetchStagingUsers}
      />
    </Container>
  );
};

export default HXCPartnerCreationPage;
